import { PropsWithChildren, ReactNode } from "react";
import styled from "@emotion/styled";
import { Badge, Variant } from "@leafygreen-ui/badge";
import { palette } from "@leafygreen-ui/palette";
import { SideNavGroup } from "@leafygreen-ui/side-nav";
import { Body } from "@leafygreen-ui/typography";
import Icon, { IconProps } from "@evg-ui/lib/components/Icon";
import { size } from "@evg-ui/lib/constants/tokens";

const { green } = palette;

interface BaseNavGroupProps<T> {
  ["data-testid"]: string;
  children: ReactNode;
  glyph: IconProps["glyph"];
  items: T[];
  navGroupTitle: string;
  defaultMessage: string;
  additionalHeaderText?: ReactNode;
}

// Trailing comma is necessary to indicate this is a generic, not a JSX open tag.
const BaseNavGroup = <T,>({
  additionalHeaderText,
  children,
  "data-testid": dataTestId,
  defaultMessage,
  glyph,
  items,
  navGroupTitle,
}: PropsWithChildren<BaseNavGroupProps<T>>) => (
  <StyledSideNavGroup
    glyph={<Icon fill={green.dark2} glyph={glyph} />}
    hasActiveItem={items.length > 0}
    header={
      <NavGroupHeader data-testid={`${dataTestId}-nav-group-header`}>
        <NavGroupTitle>{navGroupTitle}</NavGroupTitle>
        <Badge variant={Variant.Green}>{items.length}</Badge>
        {additionalHeaderText}
      </NavGroupHeader>
    }
  >
    {items.length ? (
      children
    ) : (
      <DefaultMessageWrapper data-testid={`${dataTestId}-default-message`}>
        <Body>{defaultMessage}</Body>
      </DefaultMessageWrapper>
    )}
  </StyledSideNavGroup>
);

const StyledSideNavGroup = styled(SideNavGroup)`
  > div {
    padding: 0;
  }
`;

const NavGroupHeader = styled.div`
  display: flex;
  align-items: center;
`;

const NavGroupTitle = styled.div`
  margin-right: ${size.xxs};
`;

const DefaultMessageWrapper = styled.div`
  margin-top: ${size.xs};
  margin-bottom: ${size.s};
`;

export default BaseNavGroup;
